import { useMediaQuery } from 'react-responsive'
import { useNewSidebar } from 'src/app/layouts/sidebar'

export interface ModalLayoutClasses {
  container: string
  backdrop: string
  content: string
}

export type ModalSize = 'sm' | 'md' | 'lg' | '6xl' | 'xl' | '2xl' | 'inherit'

interface UseModalLayoutOptions {
  size?: ModalSize
  autoHeight?: boolean
  fullscreenOverlay?: boolean
}

export function useModalLayout(options: UseModalLayoutOptions = {}) {
  const { size = 'lg', autoHeight = false, fullscreenOverlay = false } = options
  const { isMinimized } = useNewSidebar()
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' })

  // Size classes for modal content - responsive to sidebar state and available space
  const getSizeClasses = (): string => {
    // Base sizes for different modal sizes
    const baseSizes: Record<ModalSize, string> = {
      sm: 'tw-max-w-md',
      md: 'tw-max-w-lg',
      lg: 'tw-max-w-5xl',
      '6xl': 'tw-max-w-6xl', // 1152px - for modals needing larger than lg but smaller than xl
      xl: 'tw-max-w-7xl',
      '2xl': 'tw-max-w-full', // Use full available width for largest size
      'inherit': ''
    }

    // When sidebar is minimized, we have more space available
    // BUT: if fullscreenOverlay is enabled, always use base sizes (no expansion)
    if (isDesktop && isMinimized && !fullscreenOverlay) {
      const expandedSizes: Record<ModalSize, string> = {
        sm: 'tw-max-w-lg',
        md: 'tw-max-w-2xl',
        lg: 'tw-max-w-5xl',
        '6xl': 'tw-max-w-6xl', // 1152px - for modals needing larger than lg but smaller than xl
        xl: 'tw-max-w-7xl',
        '2xl': 'tw-max-w-full', // Use full available width
        'inherit': ''
      }
      return expandedSizes[size]
    }

    return baseSizes[size]
  }

  // Calculate positioning based on sidebar state and device
  const getPositioningClasses = (): ModalLayoutClasses => {
    if (!isDesktop) {
      // Mobile: adjust height behavior based on autoHeight
      const mobileHeightClasses = autoHeight 
        ? 'tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col tw-bg-white tw-rounded-lg'
        : 'tw-w-full tw-h-full tw-flex tw-flex-col tw-bg-white tw-rounded-lg'
        
      return {
        container: 'tw-fixed tw-inset-0 tw-z-50 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-p-4',
        backdrop: '', // No separate backdrop needed
        content: mobileHeightClasses
      }
    }

    // Desktop: CSS class-based positioning (mirrors main-content approach)
    const sizeClass = getSizeClasses()

    // Determine height classes based on autoHeight setting
    const heightClasses = autoHeight
      ? 'tw-max-h-[90vh] tw-my-auto' // Auto height with max constraint and center vertically
      : 'tw-h-full' // Full height as before

    // Fullscreen overlay: covers entire viewport and centers modal
    if (fullscreenOverlay) {
      return {
        container: 'tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50 tw-p-4',
        backdrop: '', // No separate backdrop needed
        content: `tw-bg-white tw-rounded-xl tw-shadow-2xl ${sizeClass} tw-flex tw-flex-col ${heightClasses} tw-w-full tw-z-50`
      }
    }

    // Default: respects header and sidebar positioning
    const modalPositionClass = isMinimized ? 'modal-overlay-minimized' : 'modal-overlay-expanded'
    return {
      container: `${modalPositionClass} tw-fixed tw-top-16 tw-bottom-0 tw-left-0 tw-right-0 tw-z-50 tw-bg-black tw-bg-opacity-50 tw-p-4 tw-rounded-xl`,
      backdrop: '', // No separate backdrop needed
      content: `tw-bg-white tw-rounded-xl tw-shadow-2xl ${sizeClass} tw-flex tw-flex-col ${heightClasses} tw-w-full`
    }
  }

  return {
    classes: getPositioningClasses(),
    isDesktop,
    isMinimized,
    size
  }
}

export default useModalLayout